import { useCallback, useMemo } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faArrowLeft, faArrowRight, faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getEngageSenders, getMessageNumbers, updateEngageMessageById } from 'api/engage';
import * as FilterWarning from 'assets/js/pages/engage/components/Modals/FilterWarningModal';
import * as SendWarning from 'assets/js/pages/engage/components/Modals/SendWarningModal';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { ErrorCodes } from 'constants/error-codes';
import { MessageChannel } from 'constants/messages';
import { useResponsive } from 'contexts/useResponsive';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentEngageMessage } from 'customHooks/useCurrentEngageMessage';
import { useEngageCurrentStep } from 'customHooks/useEngageCurrentStep';
import { upperFirst } from 'lodash';
import qs from 'qs';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { twMerge } from 'tailwind-merge';
import { EngageMessage } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const StepNavigation = ({ isNextDisabled = false }: { isNextDisabled?: boolean }) => {
    const { isSidebarCollapsed } = useResponsive();
    const location = useLocation();
    const history = useHistory();
    const currentStep = useEngageCurrentStep();
    const { data: message } = useCurrentEngageMessage();

    const isPrevDisabled = useMemo(() => {
        return currentStep === 1;
    }, [currentStep]);

    const setStep = useCallback(
        (newStep: number) => {
            const params = qs.stringify({ step: newStep, editor: message.editor });
            history.push(`${location.pathname}?${params}`);
        },
        [history, location.pathname, message.editor],
    );

    return (
        <div
            className={twMerge(
                'z-pentOutermost shadow-top fixed bottom-0 left-0 right-0 flex h-24 w-full items-center bg-white p-4 md:px-12 md:py-8',
                isSidebarCollapsed ? 'md:pl-12' : 'md:pl-[400px]',
            )}
        >
            <div className="col-span-4 flex basis-1/3 items-center">
                <Button
                    variant="outline"
                    color="neutral"
                    disabled={isPrevDisabled}
                    onClick={() => setStep(currentStep - 1)}
                >
                    <FontAwesomeIcon className="!ml-0 mr-2" icon={faArrowLeft} />
                    <span>Back</span>
                </Button>
            </div>

            <p className="col-span-4 basis-1/3 select-none text-center font-medium">
                Step {currentStep} of 3
            </p>

            <div className="col-span-4 flex basis-1/3 items-center justify-end">
                <NextButton
                    onClickStep={() => setStep(currentStep + 1)}
                    setStep={setStep}
                    disabled={isNextDisabled}
                />
            </div>
        </div>
    );
};

interface NextButtonProps {
    onClickStep: Function;
    setStep: (newStep: number) => void;
    disabled: boolean;
}
const NextButton = ({ onClickStep, setStep, disabled }: NextButtonProps) => {
    const currentStep = useEngageCurrentStep();
    const { error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: message } = useCurrentEngageMessage();
    const { filterGroups } = useFiltersByKey('engage');
    const hasFilters = filterGroups.some(f => f.filters.length > 0);

    const { mutate: setQueued, isPending } = useMutation<EngageMessage, GivebutterAxiosError>({
        mutationFn: () => updateEngageMessageById(accountId, message.id, { status: 'queued' }),
        onSuccess: response => {
            queryClient.setQueryData(
                accountQueryKeys.engageMessageById(accountId, message.id),
                response,
            );
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessages(accountId),
            });
        },
        onError: e => {
            if (
                e.response &&
                [ErrorCodes.FORBIDDEN, ErrorCodes.CONFLICT, ErrorCodes.RATE_LIMIT].includes(
                    e.response?.status,
                )
            ) {
                error(e.response.data.message);
            }

            setStep(currentStep - 1);
        },
    });

    const buttonDisabled = useMemo(() => {
        return (
            isPending ||
            disabled ||
            (currentStep === 2 && (!message.body || !message.sender_id)) ||
            (currentStep === 2 && message.channel === 'email' && !message.subject)
        );
    }, [currentStep, message, disabled, isPending]);

    if (currentStep === 3) {
        const buttonContent = (
            <>
                <span>
                    {!message.sending_at ? 'Send' : 'Schedule'}{' '}
                    {message.channel === MessageChannel.SMS ? 'SMS' : upperFirst(message.channel)}
                </span>

                <FontAwesomeIcon icon={faPaperPlane} />
            </>
        );

        if (!message.sending_at && !buttonDisabled) {
            return (
                <SendWarning.Root onProceed={() => setQueued()}>
                    <SendWarning.Trigger asChild>
                        <Button variant="solid" color="primary">
                            {buttonContent}
                        </Button>
                    </SendWarning.Trigger>
                </SendWarning.Root>
            );
        }

        return (
            <Button
                variant="solid"
                color="primary"
                onClick={() => setQueued()}
                disabled={buttonDisabled}
            >
                {buttonContent}
            </Button>
        );
    }

    return (
        <div className="flex flex-col items-end gap-2 md:flex-row md:items-center md:gap-4">
            <DisabledNextReasons />

            {currentStep === 1 && !hasFilters && !buttonDisabled ? (
                <FilterWarning.Root onProceed={() => onClickStep(currentStep + 1)}>
                    <FilterWarning.Trigger asChild>
                        <Button variant="outline" color="neutral">
                            <span>Next</span>
                            <FontAwesomeIcon icon={faArrowRight} />
                        </Button>
                    </FilterWarning.Trigger>
                </FilterWarning.Root>
            ) : (
                <Button
                    variant="outline"
                    color="neutral"
                    onClick={() => onClickStep(currentStep + 1)}
                    disabled={currentStep === 3 || buttonDisabled}
                >
                    <span>Next</span>
                    <FontAwesomeIcon icon={faArrowRight} />
                </Button>
            )}
        </div>
    );
};

const DisabledNextReasons = () => {
    const currentStep = useEngageCurrentStep();
    const {
        accountId,
        tasks: { engage: tasks },
    } = useCurrentAccount();
    const { data: message } = useCurrentEngageMessage();

    const { data: senders } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageSenders(accountId),
        queryFn: () => getEngageSenders(accountId),
        select: response => response.data.data,
    });

    const { data: numbers } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageNumbers(accountId),
        queryFn: () => getMessageNumbers(accountId),
    });

    const missingItemsLabel = useMemo(() => {
        let missingItems: string[] = [];
        if (!message.sender_id) missingItems.push('sender');
        if (!message.subject && message.channel === 'email') missingItems.push('subject');
        if (!message.body) missingItems.push('body');

        let missingItemsLabel = '';
        if (missingItems.length > 2) {
            missingItemsLabel = missingItems
                .map((item, index) => {
                    return index + 1 >= missingItems.length ? `and ${item}` : item;
                })
                .join(', ');
        } else {
            missingItemsLabel = missingItems.join(' and ');
        }

        return missingItemsLabel;
    }, [message]);

    if (currentStep !== 2) return null;

    if (!senders.length && message.channel === 'email') {
        return (
            <p className="max-w-xs text-right text-xs font-semibold md:text-left">
                <Link
                    to={`/accounts/${accountId}/engage/settings/senders`}
                    className={'hover:underline'}
                >
                    Add a sender
                </Link>{' '}
                to continue.
            </p>
        );
    }

    if (!numbers.length && message.channel === 'sms') {
        return (
            <p className="max-w-xs text-right text-xs font-semibold md:text-left">
                <Link
                    to={`/accounts/${accountId}/engage/settings/sms`}
                    className={'hover:underline'}
                >
                    Register an SMS number
                </Link>{' '}
                to continue.
            </p>
        );
    }

    if (!tasks.has_address) {
        return (
            <p className="max-w-xs text-right text-xs font-semibold md:text-left">
                <Link
                    to={`/accounts/${accountId}/engage/settings/addresses`}
                    className={'hover:underline'}
                >
                    Provide an address
                </Link>{' '}
                to continue.
            </p>
        );
    }

    if (missingItemsLabel) {
        return (
            <p className="max-w-xs text-right text-xs font-semibold md:text-left">
                Set a {missingItemsLabel} to continue.
            </p>
        );
    }

    return null;
};

export { StepNavigation };
